// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Флаг для предотвращения множественных одновременных запросов
    const [isFetching, setIsFetching] = useState(false);

    // Функция загрузки пользователя с защитой от дублирования
    const loadUser = useCallback(async (forceReload = false) => {
        // Если уже загружаем - не делаем новый запрос
        if (isFetching) {
            console.log('⏳ Запрос пользователя уже выполняется, пропускаем...');
            return;
        }

        // Если данные уже есть и не требуется принудительная перезагрузка
        if (user && !forceReload) {
            console.log('✅ Данные пользователя уже загружены');
            setLoading(false);
            return;
        }

        const token = localStorage.getItem('auth_token');

        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }

        try {
            setIsFetching(true);
            console.log('🔄 Загрузка данных пользователя...');

            const result = await authService.getCurrentUser();

            if (result.success) {
                setUser(result.data);
                setError(null);
                console.log('✅ Данные пользователя загружены:', result.data);
            } else {
                setUser(null);
                setError(result.message);
                console.error('❌ Ошибка загрузки пользователя:', result.message);
            }
        } catch (err) {
            setUser(null);
            setError(err.message);
            console.error('❌ Ошибка при загрузке пользователя:', err);
        } finally {
            setLoading(false);
            setIsFetching(false);
        }
    }, [user, isFetching]);

    // Загрузка пользователя при монтировании
    useEffect(() => {
        loadUser();
    }, []); // Пустой массив зависимостей - выполняется только один раз

    // Функция для обновления данных пользователя
    const refreshUser = useCallback(async () => {
        await loadUser(true); // Принудительная перезагрузка
    }, [loadUser]);

    // Функция логина
    const login = useCallback(async (credentials) => {
        try {
            setLoading(true);
            const result = await authService.login(credentials);

            if (result.success) {
                // Сразу загружаем данные пользователя после логина
                await loadUser(true);
            }

            return result;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [loadUser]);

    // Функция logout
    const logout = useCallback(() => {
        authService.logout();
        setUser(null);
        setError(null);
        console.log('👋 Пользователь вышел из системы');
    }, []);

    // Функция обновления профиля
    const updateProfile = useCallback(async (profileData) => {
        try {
            const result = await authService.updateProfile(profileData);

            if (result.success) {
                // Обновляем локальное состояние пользователя
                setUser(prevUser => ({
                    ...prevUser,
                    ...result.data
                }));
                console.log('✅ Профиль обновлен:', result.data);
            }

            return result;
        } catch (err) {
            console.error('❌ Ошибка обновления профиля:', err);
            throw err;
        }
    }, []);

    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
        updateProfile,
        isFetching, // Для отладки
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Хук для использования контекста
export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }

    return context;
};
